import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { MedicationRequest } from "@curai/js-fhir-tools";
import { useQueries } from "@tanstack/react-query";

interface PrescriptionsParams {
  patientId: string;
  encounterId?: string;
  enabled: boolean;
}

export enum RouteToPharmacyUnavailableReason {
  NOT_A_CLINICIAN = "NOT_A_CLINICIAN",
  FAILED_TO_ROUTE = "FAILED_TO_ROUTE",
  ALREADY_SENT = "ALREADY_SENT",
  MISSING_REQUIRED_DETAILS = "MISSING_REQUIRED_DETAILS",
  PHARMACY_MISSING_VENDOR_ID = "PHARMACY_MISSING_VENDOR_ID",
  PATIENT_MISSING_VENDOR_ID = "PATIENT_MISSING_VENDOR_ID",
}

export enum MissingPatientDetails {
  FIRST_NAME = "first-name",
  LAST_NAME = "last-name",
  DATE_OF_BIRTH = "date-of-birth",
  GENDER = "gender",
  ADDRESS = "address",
  CITY = "city",
  STATE = "state",
  ZIP = "zip",
  PHONE = "phone",
}

export interface RxItem {
  medicationRequest: MedicationRequest;
  canEdit: boolean;
  canCancel: boolean;
  canMarkPhonedIn: boolean;
  canSendToPharmacy: boolean;
  routeToPharmacyUnavailableReasons: RouteToPharmacyUnavailableReason[];
  isSentToPharmacy: boolean;
  canRetrySendToPharmacy: boolean;
  hasInteraction: boolean;
}

interface PrescriptionsResponse {
  heightWeightNeeded: boolean;
  canSendAllToPharmacy: boolean;
  registeredWithErxVendor: boolean;
  rxs: RxItem[];
  prescriptions: MedicationRequest[];
  missingPatientDetails?: MissingPatientDetails[];
}

const queryFn = async (encounterId: string) => {
  const response = await axiosInstance.get<PrescriptionsResponse>(
    "/rx/prescriptions/",
    { params: { encounterId } },
  );

  return {
    ...response.data,
    rxs: response.data.rxs.map((rx) => ({
      ...rx,
      medicationRequest: new MedicationRequest(rx.medicationRequest),
    })),
  };
};

export const usePrescriptions = ({
  patientId,
  encounterId,
  enabled,
}: PrescriptionsParams) => {
  return useApi<PrescriptionsResponse>({
    queryOptions: {
      enabled: Boolean(enabled && encounterId),
      queryFn: async () => {
        return await queryFn(encounterId as string);
      },
      queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT(
        patientId,
        encounterId as string,
      ),
    },
  });
};

export interface PrescriptionRoutabilityBuckets {
  sendable: RxItem[];
  retryable: RxItem[];
  notSendable: RxItem[];
}

interface PrescriptionsByRoutableAction extends PrescriptionRoutabilityBuckets {
  isError: boolean;
  isFetching: boolean;
  isPending: boolean;
}

export const getPrescriptionRoutabilityBuckets = (
  rxs: RxItem[],
): PrescriptionRoutabilityBuckets => {
  return {
    sendable: rxs.filter((rx) => rx.canSendToPharmacy),
    retryable: rxs.filter(
      (rx) => !rx.canSendToPharmacy && rx.canRetrySendToPharmacy,
    ),
    notSendable: rxs.filter(
      (rx) =>
        !rx.canSendToPharmacy &&
        !rx.canRetrySendToPharmacy &&
        !rx.isSentToPharmacy,
    ),
  };
};

export const usePrescriptionsMultipleEncounters = ({
  patientId,
  encounterIds,
}: {
  patientId: string;
  encounterIds: string[];
}) => {
  return useQueries({
    queries: encounterIds.map((encounterId) => ({
      queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT(
        patientId,
        encounterId,
      ),
      queryFn: async () => {
        return await queryFn(encounterId);
      },
    })),
    combine: (results): PrescriptionsByRoutableAction => {
      const rxs = results.flatMap((result) => result.data?.rxs ?? []);
      const prescriptionBuckets = getPrescriptionRoutabilityBuckets(rxs);

      return {
        ...prescriptionBuckets,
        isError: results.some((result) => result.isError),
        isFetching: results.some((result) => result.isFetching),
        isPending: results.some((result) => result.isPending),
      };
    },
  });
};
